import { useNavigate, useParams } from 'react-router-dom'

import { Box, Icon, Text, Touchable } from '@factorialco/deprecated-design-system'
import DOMPurify from 'dompurify'
import { AtsMessageBoardConversationFragment } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { dateBackendDateTimeToRelativeTime } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useArchiveConversation } from 'modules/ats/hooks/conversations/useArchiveConversation'
import { isConversationRead } from 'modules/ats/lib/utils/conversation'

interface Props {
  conversation: AtsMessageBoardConversationFragment
}

export const ConversationsItem = ({ conversation }: Props) => {
  const { access } = useCurrentSession()
  const accessId = access.id
  const { loading: loadingArchive, archiveConversation } = useArchiveConversation()
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const params = useParams()
  const conversationId = String(params.conversation_id)

  const statusActive = !conversation.conversationStatus?.archivedAt
  const selected = conversationId === conversation.id
  const statusRead = isConversationRead(conversation)

  const seeConversationUrl = navigation.ats.messages.conversation.fullPath({
    conversation_id: conversation.id,
  })

  const onArchiveConversation = async () => {
    await archiveConversation({
      id: conversation.id,
      accessId: accessId,
    })

    app.setFlash({
      type: 'correct',
      message: i18n.t('ats.messages.archive_conversation_success_message'),
      time: 2000,
    })
  }

  return (
    <Box
      component="li"
      onClick={() => navigate(seeConversationUrl)}
      cursor="pointer"
      background={statusRead || selected ? 'grey200' : 'grey000White'}
      paddingY="s12"
      paddingX="s16"
      gap="s4"
      border={{
        bottom: {
          color: 'grey400',
          width: 's1',
          style: 'solid',
        },
      }}
    >
      <Box flexDirection="row" gap="s16" justifyContent="spaceBetween" alignItems="center">
        <Box flexDirection="row" gap="s8" alignItems="center" flexGrow>
          {!statusRead && <Icon.Tiny icon="circle" color="champagne1000" />}
          <Text.Normal weight="medium" color={statusRead ? 'grey800' : 'grey1000'}>
            {conversation.application.candidate.fullName}
          </Text.Normal>
        </Box>
        {conversation?.lastMessage?.createdAt && (
          <Text.Normal size="200">
            {dateBackendDateTimeToRelativeTime(conversation.lastMessage.createdAt)}
          </Text.Normal>
        )}
        {statusActive && (
          <Touchable
            onClick={(event: React.SyntheticEvent) => {
              event.stopPropagation()
              onArchiveConversation()
            }}
            disabled={loadingArchive}
          >
            <Box padding="s4">
              <Icon.Small icon="close" color="grey800" />
            </Box>
          </Touchable>
        )}
      </Box>
      <Text.Normal size="200" color={statusRead ? 'grey800' : 'grey1000'}>
        {conversation.application.jobPosting.location
          ? i18n.t('ats.messages.messages_table.job_posting_location', {
              job_posting: conversation.application.jobPosting.title,
              location: conversation.application.jobPosting.location.name,
            })
          : conversation.application.jobPosting.title}
      </Text.Normal>
      {conversation?.lastMessage?.content && (
        <Box paddingTop="s4" maxHeight="s48" overflowY="hidden">
          <Text.Normal size="200" color={statusRead ? 'grey700' : 'grey800'}>
            {DOMPurify.sanitize(conversation.lastMessage.content || '', {
              USE_PROFILES: { html: false },
            })}
          </Text.Normal>
        </Box>
      )}
    </Box>
  )
}
